import { useLingui } from "@lingui/react/macro";
import { Button } from "@rakazo/ui-web";
import { Maximize, Minimize } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { errorText } from "../lib/user-error";

/** Fullscreen changes presentation without replacing the authorized viewer. */
export function ComputerScreenViewport({ children }: { children: ReactNode }) {
  const { t } = useLingui();
  const viewport = useRef<HTMLDivElement>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const node = viewport.current;
    const changed = () => setFullscreen(document.fullscreenElement === node);
    document.addEventListener("fullscreenchange", changed);
    return () => {
      document.removeEventListener("fullscreenchange", changed);
      if (document.fullscreenElement === node)
        void document.exitFullscreen().catch(() => undefined);
    };
  }, []);
  async function toggle() {
    setError("");
    try {
      if (document.fullscreenElement === viewport.current) await document.exitFullscreen();
      else await viewport.current?.requestFullscreen();
    } catch (e) {
      setError(errorText(e));
    }
  }
  const label = fullscreen ? t`Exit fullscreen` : t`Fullscreen`;
  return (
    <div ref={viewport} className="relative min-h-0 flex-1 bg-background">
      {children}
      {document.fullscreenEnabled && (
        <Button
          variant="outline"
          size="icon"
          className="absolute right-3 top-3 z-10"
          aria-label={label}
          title={label}
          onClick={() => void toggle()}
        >
          {fullscreen ? <Minimize /> : <Maximize />}
        </Button>
      )}
      {error && (
        <p
          role="alert"
          className="absolute bottom-3 left-3 right-3 bg-background p-2 text-sm text-destructive"
        >
          {error}
        </p>
      )}
    </div>
  );
}
